// Princeton University: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
// #3859 (2026-10-11), page vs KB: the cost table now follows the school's
// own 2026-27 cost of attendance page (cited as its own source), the figures
// the KB holds; the Common Data Set G1 budget it showed before differs.
//  - Princeton's Common Data Set 2025-2026 (CDS_2526_Princeton_v2.pdf from
//    ir.princeton.edu): C1 applicants/admits/enrollees for Fall 2025 with the
//    residency table; C8 test policy for Fall 2027; C21/C22 early plans;
//    G1/G5 2026-2027 costs.
//  - admission.princeton.edu (first-year dates and deadlines, standardized
//    testing). Princeton stays test-optional for fall 2027 entry and returns
//    to requiring the SAT or ACT for fall 2028 entry.
//  - College Scorecard API, school id 186131 (net price by family income).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Princeton Common Data Set 2025-2026',
        url: 'https://ir.princeton.edu/sites/g/files/toruqf2041/files/documents/CDS_2526_Princeton_v2.pdf',
        read: '2026-09-30',
    },
    deadlines: {
        label: 'Princeton Admission, Application Dates and Deadlines',
        url: 'https://admission.princeton.edu/apply/first-year-application-dates-deadlines',
        read: '2026-09-30',
    },
    tests: {
        label: 'Princeton Admission, Standardized Testing',
        url: 'https://admission.princeton.edu/apply/standardized-testing',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?186131-Princeton-University',
        read: '2026-10-15',
    },
    coa: {
        label: 'Princeton Admission, Fees & Payment Options (2026-27 student budget)',
        url: 'https://admission.princeton.edu/cost-aid/fees-payment-options',
        read: '2026-10-11',
    },
};

const college = {
    slug: 'princeton-university',
    kbId: 'princeton_university',
    name: 'Princeton University',
    shortName: 'Princeton',
    location: 'Princeton, NJ',
    control: 'Private',
    lastChecked: '2026-10-11',
    sources: SOURCES,
    // From the KB's sourced branding (Princeton graphic identity); decoration only.
    branding: {
        primary: '#EE7F2D',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/princeton_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 42303,
        admitted: 1868,
        enrolled: 1408,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 4602, admitted: 229 },
            { group: 'Out-of-state', applied: 27134, admitted: 1398 },
            { group: 'International', applied: 10567, admitted: 241 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Princeton\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by degree or department.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Single-choice early action',
        note: 'Princeton has no early decision plan, and the Common Data Set does not report how many applied or were admitted in early action, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Single-Choice Early Action', deadline: 'November 1', decision: 'Mid-December', binding: false },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'Late March', binding: false },
        ],
        note: 'Admitted in either round, you have until May 1 to reply. A graded written paper is due with the application.',
        source: 'deadlines',
        binding: 'Single-choice early action is not binding: if admitted, you have until May 1 to decide. If you apply this way, you may not apply to an early program at any other private college or university in the United States. You may still apply early to any public institution or service academy, to any institution outside the United States, and to any college with a nonbinding rolling process, as long as the decision is nonbinding.',
        bindingSource: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Princeton is test-optional for students applying in the 2026-27 cycle to enroll in fall 2027. If you apply without an SAT or ACT score, you are not at a disadvantage. If you send scores, send them directly from the testing agency.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [
            { topic: 'Testing', line: 'Princeton requires the SAT or ACT for fall 2028 entry, starting with the 2027-28 admission cycle.', source: 'tests' },
        ],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 68140 },
            { item: 'Housing', amount: 13010 },
            { item: 'Food', amount: 9110 },
            { item: 'Fees', amount: 314 },
            { item: 'Books, course materials, supplies and equipment', amount: 1050 },
            { item: 'Personal expenses', amount: 3000 },
        ],
        total: 94624,
        note: 'Princeton\'s 2026-27 student budget. Transportation varies and is not in the total.',
        source: 'coa',
        netPrice: {
            average: 7815,
            byIncome: [
                { income: '$0 to $30,000', amount: null },
                { income: '$30,001 to $48,000', amount: 795 },
                { income: '$48,001 to $75,000', amount: 1346 },
                { income: '$75,001 to $110,000', amount: 4761 },
                { income: 'Over $110,000', amount: 38927 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. The Department publishes no figure for the $0 to $30,000 band. Your own price comes from Princeton\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
